import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { PaidGate } from "@/components/PaidGate";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Mail,
  Package,
  Send,
  CheckCircle2,
  Clock,
  Truck,
  RotateCw,
  ArrowUpRight,
  ExternalLink,
} from "lucide-react";

const STATUS_META = {
  submitted: { icon: Send, color: "text-[#A8ADA5]", label: "Queued" },
  in_transit: { icon: Truck, color: "text-[#7CFF9B]", label: "In transit" },
  processed_for_delivery: { icon: Truck, color: "text-[#7CFF9B]", label: "Out for delivery" },
  delivered: { icon: CheckCircle2, color: "text-[#D5FF3F]", label: "Delivered" },
  returned_to_sender: { icon: RotateCw, color: "text-[#FF6B4A]", label: "Returned" },
  failed: { icon: RotateCw, color: "text-[#FF6B4A]", label: "Failed" },
};

const money = (cents) => `$${(cents / 100).toFixed(2)}`;

function daysAgo(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const ms = Date.now() - d.getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

export default function MailPage() {
  const { user } = useAuth();
  const paid = user?.plan === "basic" || user?.plan === "plus";
  const [quota, setQuota] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [followingUp, setFollowingUp] = useState(null);

  const load = async () => {
    if (!paid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [q, h] = await Promise.all([api.get("/mail/quota"), api.get("/mail/history")]);
      setQuota(q.data);
      setHistory(h.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [paid]);

  const buyPack = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/mail/buy-credits", {
        quantity: 1,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBuying(false);
    }
  };

  const sendFollowup = async (m) => {
    setFollowingUp(m.id);
    try {
      const { data } = await api.post(`/mail/${m.id}/followup`);
      if (data.needs_address) {
        toast.warning("Follow-up drafted. Open the dispute to add your address and send.");
      } else {
        toast.success("Follow-up escalation sent");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setFollowingUp(null);
    }
  };

  return (
    <AppShell>
      <div className="mb-10">
        <div className="overline mb-3 text-[#D5FF3F]">Certified Mail</div>
        <h1 className="font-display text-5xl md:text-6xl leading-none">
          Sent by Aura.
          <span className="text-[#D5FF3F]"> Signed by USPS.</span>
        </h1>
        <p className="text-[#A8ADA5] mt-3 max-w-2xl">
          Every dispute you send through Aura goes out as USPS Certified Mail with electronic return
          receipt. Tracking, delivery confirmation, and 30-day follow-up automation — all done for you.
        </p>
      </div>

      {!paid ? (
        <PaidGate
          toolName="Auto-mail via USPS"
          tagline="Aura Basic or Plus unlocks certified mail. $12 per letter goes toward USPS Certified Mail with Return Receipt — no quotas, no monthly caps."
        />
      ) : (
        <>
          {/* Per-letter price + trust callouts (no more monthly quotas). */}
          <div className="grid md:grid-cols-3 gap-4 mb-10">
            <div className="card-surface p-6" data-testid="mail-price-card">
              <div className="overline mb-2 text-[#D5FF3F]">Per letter</div>
              <div className="ticker text-4xl text-[#F5F3EE]">
                {loading || !quota ? "—" : money(quota.price_per_letter_cents || 1200)}
              </div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#6D716C] mt-1">
                Charged before every send
              </div>
            </div>
            <div className="card-surface p-6" data-testid="mail-tracking-card">
              <div className="overline mb-2 text-[#7CFF9B]">USPS Certified</div>
              <div className="ticker text-4xl text-[#7CFF9B]">Tracking</div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#6D716C] mt-1">
                Every letter · return receipt
              </div>
            </div>
            <div className="card-surface p-6" data-testid="mail-sent-count-card">
              <div className="overline mb-2">Your sends</div>
              <div className="ticker text-4xl text-[#F5F3EE]">
                {loading ? "—" : history.length}
              </div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#6D716C] mt-1">
                Total letters mailed
              </div>
            </div>
          </div>

          {quota?.lob_configured === false && (
            <div className="card-surface p-5 mb-8 border-[#FF6B4A]/40 bg-[#FF6B4A]/5">
              <div className="text-sm text-[#FF6B4A] font-mono">
                Live mail sending is not enabled yet. Ask your admin to add a Lob API key in the
                backend .env (LOB_API_KEY).
              </div>
            </div>
          )}

          {/* History */}
          <div className="mb-4 flex items-end justify-between">
            <div>
              <div className="overline mb-2">History</div>
              <h2 className="font-display text-3xl">Every letter, tracked.</h2>
            </div>
          </div>

          {loading ? (
            <div className="text-[#A8ADA5]">Loading…</div>
          ) : history.length === 0 ? (
            <div className="card-surface p-10 text-center" data-testid="mail-history-empty">
              <Mail size={30} className="text-[#A8ADA5] mx-auto mb-4" />
              <div className="font-display text-2xl mb-2">No sent letters yet.</div>
              <p className="text-sm text-[#A8ADA5] mb-6 max-w-md mx-auto">
                Draft a dispute, then hit <span className="text-[#D5FF3F]">Send Certified Mail</span> from any
                dispute page. Aura handles the paperwork.
              </p>
              <Link to="/app/disputes">
                <Button className="rounded-full btn-lime h-10 px-5">
                  Go to disputes <ArrowUpRight size={14} className="ml-1.5" />
                </Button>
              </Link>
            </div>
          ) : (
            <div className="card-surface overflow-hidden" data-testid="mail-history-list">
              <div className="divide-y divide-[#26282C]">
                {history.map((m) => {
                  const meta = STATUS_META[m.status] || STATUS_META.submitted;
                  const Icon = meta.icon;
                  const delivered = m.status === "delivered";
                  const days = delivered ? daysAgo(m.delivered_at) : null;
                  const eligibleFollowup =
                    delivered && days != null && days >= 30 && !m.follow_up_mail_id;
                  return (
                    <div
                      key={m.id}
                      className="p-5 flex items-center justify-between gap-4 flex-wrap"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Icon size={18} className={meta.color} />
                        <div className="min-w-0">
                          <div className="font-medium truncate">
                            {m.bureau?.toUpperCase() || "Bureau"} · Certified
                          </div>
                          <div className="text-xs text-[#6D716C] font-mono truncate">
                            {new Date(m.created_at).toLocaleDateString()}
                            {m.tracking_number ? (
                              <> · <span className="text-[#D5FF3F]" data-testid={`mail-tracking-${m.id}`}>{m.tracking_number}</span></>
                            ) : (
                              <> · tracking pending</>
                            )}
                            {" · "}
                            {money(m.cost_cents || 1200)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest ${
                            delivered
                              ? "bg-[#D5FF3F] text-[#0B0C0E]"
                              : "bg-[#17191C] text-[#A8ADA5]"
                          }`}
                        >
                          {meta.label}
                        </span>
                        {m.tracking_number && (
                          <a
                            href={`https://tools.usps.com/go/TrackConfirmAction?tLabels=${m.tracking_number}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#A8ADA5] hover:text-[#D5FF3F] font-mono flex items-center gap-1"
                          >
                            USPS <ExternalLink size={11} />
                          </a>
                        )}
                        {eligibleFollowup && (
                          <Button
                            onClick={() => sendFollowup(m)}
                            disabled={followingUp === m.id}
                            data-testid={`mail-followup-btn-${m.id}`}
                            className="rounded-full btn-outline h-8 px-3 text-xs"
                          >
                            {followingUp === m.id ? (
                              "…"
                            ) : (
                              <>
                                <Clock size={11} className="mr-1" /> Send 30-day follow-up
                              </>
                            )}
                          </Button>
                        )}
                        {m.follow_up_mail_id && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#7CFF9B]/15 text-[#7CFF9B]">
                            Follow-up sent
                          </span>
                        )}
                        <Link
                          to={`/app/disputes/${m.dispute_id}`}
                          className="text-xs text-[#A8ADA5] hover:text-[#D5FF3F] font-mono"
                        >
                          view →
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </AppShell>
  );
}
